import { useCallback, useState } from 'react';
import type { FileRejection } from 'react-dropzone';
import { isDuplicateFileInSession } from '../../../data/receiving';
import { useAsyncAction } from '../../../hooks/use-async-action';

export const MAX_FILE_SIZE_BYTES = 25 * 1024 * 1024;

export type FileRow = {
  id: string;
  file: File;
  // 'duplicate': the upload was refused because these exact bytes are already
  // in this session (ORD-451). Not a retryable failure, so it is kept separate
  // from 'failed' (no Retry button), but it gates Process the same way: the
  // owner has to read why the file was dropped and remove the row themselves,
  // or the message flashes past as Process auto-runs. `duplicateMessage`
  // carries the server's explanation, shown verbatim rather than the generic
  // failed-row copy.
  status: 'pending' | 'uploading' | 'done' | 'failed' | 'duplicate';
  duplicateMessage?: string;
};

const DEFAULT_DUPLICATE_MESSAGE = 'This file is already in this receiving.';

// Both upload paths (batch and single retry) land on the same outcome for a
// given thrown error — pulled out so the two call sites can't drift.
function uploadOutcome(err: unknown): {
  status: 'failed' | 'duplicate';
  duplicateMessage?: string;
} {
  if (isDuplicateFileInSession(err)) {
    return {
      status: 'duplicate',
      duplicateMessage: err.body?.message ?? DEFAULT_DUPLICATE_MESSAGE,
    };
  }

  return { status: 'failed' };
}

type Params = {
  vendorName: string;
  acceptsCopy: string;
  onUploadFile: (file: File) => Promise<void>;
  onProcess: () => Promise<void>;
};

export function useInvoiceUpload({ vendorName, acceptsCopy, onUploadFile, onProcess }: Params) {
  const [rows, setRows] = useState<FileRow[]>([]);
  const [dropError, setDropError] = useState<string | null>(null);
  const [processError, setProcessError] = useState<string | null>(null);

  const { pending: processing, run: runProcess } = useAsyncAction(onProcess, {
    errorLabel: 'receiving: process failed',
    setError: setProcessError,
  });

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      const oversized = rejected.find((r) => r.file.size > MAX_FILE_SIZE_BYTES);
      if (oversized) {
        setDropError(`${oversized.file.name} is too large: max 25 MB per file.`);

        return;
      }

      if (rejected.length > 0) {
        setDropError(`${rejected[0].file.name} isn't an ${vendorName} format: try ${acceptsCopy}.`);

        return;
      }

      setDropError(null);
      setRows((prev) => {
        const seen = new Set(prev.map((r) => r.file.name));
        const additions = accepted
          .filter((f) => !seen.has(f.name))
          .map<FileRow>((file) => ({ id: crypto.randomUUID(), file, status: 'pending' }));

        return [...prev, ...additions];
      });
    },
    [vendorName, acceptsCopy]
  );

  const removeRow = useCallback((id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
    setDropError(null);
  }, []);

  const handleProcessClick = useCallback(async () => {
    const batch = rows.filter((r) => r.status !== 'done');
    if (batch.length === 0) {
      await runProcess();

      return;
    }

    const batchIds = new Set(batch.map((r) => r.id));
    setRows((prev) => prev.map((r) => (batchIds.has(r.id) ? { ...r, status: 'uploading' } : r)));

    const results = await Promise.all(
      batch.map(async (row) => {
        try {
          await onUploadFile(row.file);

          return {
            id: row.id,
            status: 'done' as const,
            duplicateMessage: undefined as string | undefined,
          };
        } catch (err) {
          return { id: row.id, ...uploadOutcome(err) };
        }
      })
    );
    const byId = new Map(results.map((res) => [res.id, res]));

    setRows((prev) =>
      prev.map((r) => {
        const res = byId.get(r.id);
        if (!res) {
          return r;
        }

        return { ...r, status: res.status, duplicateMessage: res.duplicateMessage };
      })
    );

    // Only a batch that went through cleanly auto-runs Process. A failed row
    // waits for Retry; a duplicate-refused row waits to be removed.
    if (results.every((res) => res.status === 'done')) {
      await runProcess();
    }
  }, [rows, onUploadFile, runProcess]);

  const handleRetry = useCallback(
    async (id: string) => {
      const target = rows.find((r) => r.id === id);
      if (!target) {
        return;
      }

      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, status: 'uploading' } : r)));

      let outcome: { status: 'done' | 'failed' | 'duplicate'; duplicateMessage?: string } = {
        status: 'done',
      };
      try {
        await onUploadFile(target.file);
      } catch (err) {
        // A 'failed' outcome is surfaced as the row-level "Upload failed —
        // please retry" message; a 'duplicate' outcome shows the server's
        // message instead. Either one keeps Process gated below.
        outcome = uploadOutcome(err);
      }

      setRows((prev) =>
        prev.map((r) =>
          r.id === id
            ? { ...r, status: outcome.status, duplicateMessage: outcome.duplicateMessage }
            : r
        )
      );

      if (outcome.status !== 'done') {
        return;
      }

      // rows is the closure snapshot from before the retry: every *other* row was
      // either already 'done', 'duplicate', or 'failed' when the handler started.
      // If all others are 'done' and this retry just succeeded, every row is
      // now done.
      const allDone = rows.every((r) => r.id === id || r.status === 'done');
      if (allDone) {
        await runProcess();
      }
    },
    [rows, onUploadFile, runProcess]
  );

  return {
    rows,
    dropError,
    processing,
    processError,
    onDrop,
    removeRow,
    runProcess,
    handleProcessClick,
    handleRetry,
  };
}
